import type { RequestStatus } from '@features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogOtherCostTypeData, ProjectMethodologyEnum } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { otherCostTypesGql } from '../CatalogOthersTypeQueries';

interface ReturnValue {
    typesData: Omit<CatalogOtherCostTypeData, '__typename'>[];
    typesStatus: RequestStatus;
    updateTypesData: () => void;
}

export const useGetTypesData = ({
    methodology,
    withEffect = true,
}: {
    methodology: ProjectMethodologyEnum;
    withEffect?: boolean;
}): ReturnValue => {
    const dispatch = useDispatch();

    const variables = useMemo(
        () => ({
            methodology,
            order: { id: SortEnumType.Desc },
        }),
        [methodology],
    );

    const [resetCatalogOtherTypes] = useResetStoreData({
        requestId: 'catalogOtherTypes',
    });

    const [typesData, typesStatus, , typesRequest] = useRequest({
        requestId: 'catalogOtherTypes',
        query: otherCostTypesGql,
        variables,
        withEffect,
    });

    const updateTypesData = useCallback(() => {
        dispatch(typesRequest(variables));
    }, [dispatch, typesRequest, variables]);

    useEffect(
        () => () => {
            resetCatalogOtherTypes(undefined);
        },
        [resetCatalogOtherTypes],
    );

    const sortedTypesData = useMemo(
        () =>
            [...(typesData?.otherCostTypes ?? [])].sort((a, b) => {
                if (a.isSystem === b.isSystem) {
                    return a.name.localeCompare(b.name);
                }

                return a.isSystem ? 1 : -1;
            }),
        [typesData?.otherCostTypes],
    );

    return useMemo(
        () => ({
            typesData: sortedTypesData,
            typesStatus,
            updateTypesData,
        }),
        [sortedTypesData, typesStatus, updateTypesData],
    );
};
